/* vm-infinite-scroll-brand.js — SEO-friendly infinite scroll for brand collection pages
 *
 * Infinite scroll for customers, real pagination for crawlers (Ricky's version, reinstated
 * Sep 24 2026 on Gil's instruction, Asana 1218034917020693; auto-loads on scroll).
 *
 * - The server renders the ?page=N pager VISIBLE (`--shown`), so it works with JS off.
 *   Once this script is running it drops `--shown`, which leaves the `--seo` base class:
 *   visually clipped, still in the DOM, still keyboard-reachable (:focus-within reveals
 *   it). Do NOT delete the .vm-brand-pagination--seo nav or display:none it — those
 *   <a href> links are how crawlers find the paginated URLs.
 * - Next-page URLs come from the server's own `#vm-brand-inf-next` link, so they carry
 *   every active filter and sort param (incl. the parents-only is_series_parent filter).
 * - As the shopper scrolls, history.replaceState keeps the address bar and <title> on the
 *   page they are looking at, so a shared or refreshed URL lands on the same products.
 *   The canonical <link> is never touched.
 * - WHEN it loads (Sep 28 2026 red-team fixes): one page per real scroll gesture. Every
 *   fetch needs a fresh user input (wheel, touch, a scroll key, or a pointer press on the
 *   page scrollbar — not a click on a link or card) since the previous fetch started, AND the sentinel under the grid must be
 *   above the viewport bottom + 600px. So:
 *     - a crawler render (which grows the viewport but never scrolls or types) loads
 *       nothing, and page 1 stays page 1 for Google;
 *     - loads never chain on their own. Scroll anchoring keeps the footer in view as cards
 *       are inserted above it, so "sentinel still near" chained 18 pages (~7 MB) off a
 *       single End key before;
 *     - a fast jump past the sentinel (scrollbar drag, fling) still loads, because the
 *       check has no lower bound;
 *     - typing in a field (quote form, search) or anything inside an open modal never
 *       counts: keys only count when they would scroll the page itself.
 * - On ?page=2+ the pager stays visible, so a refreshed or shared deep URL can still get
 *   back to the earlier pages with a mouse.
 * - Screen readers hear "Loaded N more products, page X of Y" and the end text through
 *   #vm-brand-inf-status, a live region that is never `hidden`.
 * - Only `.vm-brand-pcard` nodes are appended; no second H1, no second JSON-LD block.
 *   Add to Cart on appended cards works through the document-level delegated handler in
 *   sections/brand-products.liquid (window.__vmAtcDelegated), not here.
 */
(function () {
  var grid = document.querySelector('.vm-brand-products__grid');
  var nextLink = document.getElementById('vm-brand-inf-next');
  if (!grid || !nextLink) return;

  function pageNumberOf(url) {
    var m = /[?&]page=(\d+)/.exec(url);
    return m ? parseInt(m[1], 10) : 1;
  }

  var pager = document.querySelector('.vm-brand-pagination--seo');
  if (pager && pageNumberOf(window.location.href) === 1) pager.classList.remove('vm-brand-pagination--shown');

  var loading = false;
  var nextUrl = nextLink.href;
  var canUseHistory = !!(window.history && window.history.replaceState);

  var status = document.getElementById('vm-brand-inf-status');
  var totalPages = status ? parseInt(status.getAttribute('data-total-pages'), 10) : NaN;
  function announce(text) {
    if (!status) return;
    // Clear first so an identical message is still announced.
    status.textContent = '';
    window.setTimeout(function () { status.textContent = text; }, 50);
  }

  // One entry per loaded batch: its URL, title and first card (the scroll marker).
  var pages = [];
  var activeIndex = 0;

  function registerBatch(url, title, firstCard, cards) {
    var num = pageNumberOf(url);
    for (var i = 0; i < cards.length; i++) cards[i].setAttribute('data-vm-page', num);
    if (!firstCard) return;
    pages.push({ url: url, title: title, page: num, marker: firstCard });
  }

  var initialCards = grid.querySelectorAll('.vm-brand-pcard');
  registerBatch(window.location.href, document.title,
    initialCards.length ? initialCards[0] : null, initialCards);

  var sentinel = document.createElement('div');
  sentinel.className = 'vm-brand-inf-sentinel';
  grid.parentNode.insertBefore(sentinel, grid.nextSibling);

  var loader = document.getElementById('vm-brand-inf-loader');

  function showSpinner() { if (loader) { loader.hidden = false; loader.classList.add('active'); } }
  function hideSpinner() { if (loader) { loader.classList.remove('active'); loader.hidden = true; } }

  // No lower bound on purpose: a sentinel already scrolled PAST (fast jump) still counts.
  // Runaway loading is prevented by the input gate below, not by this check.
  function sentinelNearViewport() {
    return sentinel.getBoundingClientRect().top < window.innerHeight + 600;
  }

  // Input gate: inputSeq counts real user scroll gestures; a fetch consumes the value it
  // started at, so the next fetch needs a NEW gesture.
  var inputSeq = 0;
  var usedSeq = 0;
  // A modal or drawer (RFQ form, mobile menu, cart) locks the page with
  // body.style.overflow = 'hidden'; nothing that happens inside it is page scrolling.
  function pageLocked() {
    return document.body.style.overflow === 'hidden' ||
      window.getComputedStyle(document.documentElement).overflowY === 'hidden';
  }
  function markInput() { if (pageLocked()) return; inputSeq++; queueCheck(); }
  window.addEventListener('wheel', markInput, { passive: true });
  window.addEventListener('touchmove', markInput, { passive: true });
  // A press on the page scrollbar is dispatched to <html>; a click on a link, card or
  // pager button is not a scroll gesture and must not fetch a page.
  window.addEventListener('pointerdown', function (e) {
    if (e.target === document.documentElement) markInput();
  }, { passive: true });

  // Keys count only when they would actually scroll the page (Sep 28 2026 red-team):
  // typing a space in the quote form or the header search used to load a page per
  // keystroke. What a key does depends on WHICH key and WHERE focus is, so it is decided
  // per key:
  //   Space            → activates buttons, <summary>, checkboxes and widgets; only
  //                      scrolls from the page itself or a link.
  //   ArrowDown        → moves inside text fields, selects and list/menu/slider widgets;
  //                      scrolls from anything else (including a button).
  //   PageDown / End   → only text-editing fields and selects keep them; from a button
  //                      they still scroll the page, so they count there.
  // Never with a modifier (Shift+Space scrolls UP), never if a handler consumed the key,
  // never inside a dialog.
  var TEXT_FIELD = 'textarea, select, [contenteditable]:not([contenteditable="false"]),' +
    ' input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"])' +
    ':not([type="reset"]):not([type="image"]):not([type="file"]):not([type="color"]),' +
    ' [role="textbox"], [role="combobox"], [role="searchbox"], [role="spinbutton"]';
  var KEY_OWNERS = {
    ' ': TEXT_FIELD + ', input, button, summary, [role="button"], [role="checkbox"],' +
      ' [role="radio"], [role="switch"], [role="tab"], [role="option"], [role="menuitem"],' +
      ' [role="listbox"], [role="slider"], [role="treeitem"]',
    ArrowDown: TEXT_FIELD + ', input[type="radio"], input[type="range"], [role="listbox"],' +
      ' [role="menu"], [role="menubar"], [role="slider"], [role="radiogroup"],' +
      ' [role="tablist"], [role="tree"], [role="grid"]',
    PageDown: TEXT_FIELD + ', [role="listbox"], [role="slider"], [role="grid"]',
    End: TEXT_FIELD + ', [role="listbox"], [role="slider"], [role="grid"], [role="menu"], [role="tablist"]'
  };
  KEY_OWNERS.Spacebar = KEY_OWNERS[' '];
  var IN_DIALOG = 'dialog, [role="dialog"], [role="alertdialog"], [aria-modal="true"]';
  function keyScrollsPage(e) {
    var owners = KEY_OWNERS[e.key];
    if (!owners || e.defaultPrevented) return false;
    if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return false;
    var el = document.activeElement;
    if (!el || el === document.body || el === document.documentElement || !el.closest) return true;
    return !el.closest(IN_DIALOG) && !el.closest(owners);
  }
  window.addEventListener('keydown', function (e) { if (keyScrollsPage(e)) markInput(); }, { passive: true });

  function maybeLoad() {
    if (loading || !nextUrl || inputSeq <= usedSeq) return;
    if (sentinelNearViewport()) loadMore();
  }

  var checkQueued = false;
  function queueCheck() {
    if (checkQueued) return;
    checkQueued = true;
    window.requestAnimationFrame(function () { checkQueued = false; maybeLoad(); });
  }

  var syncQueued = false;
  function queueSync() {
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(function () { syncQueued = false; syncUrlToViewport(); });
  }

  function syncUrlToViewport() {
    if (!canUseHistory || pages.length < 2) return;
    var threshold = window.innerHeight * 0.33;
    var idx = 0;
    for (var i = 0; i < pages.length; i++) {
      var marker = pages[i].marker;
      if (!marker || !marker.isConnected) continue;
      if (marker.getBoundingClientRect().top <= threshold) idx = i;
      else break;
    }
    if (idx === activeIndex) return;
    activeIndex = idx;
    var p = pages[idx];
    try {
      window.history.replaceState({ vmPage: p.page }, '', p.url);
      if (p.title) document.title = p.title;
    } catch (e) {}
  }

  window.addEventListener('scroll', function () { queueSync(); queueCheck(); }, { passive: true });
  window.addEventListener('resize', queueSync, { passive: true });

  function finish() {
    nextUrl = null;
    if (!loader) return;
    loader.classList.remove('active');
    loader.hidden = false;
    var endText = loader.getAttribute('data-end-text') || 'All products loaded';
    var p = document.createElement('p');
    p.className = 'vm-brand-inf-end';
    p.textContent = endText;
    loader.textContent = '';
    loader.appendChild(p);
    announce(endText);
  }

  function loadMore() {
    if (loading || !nextUrl) return;
    loading = true;
    usedSeq = inputSeq;
    showSpinner();
    var loadedUrl = nextUrl;

    fetch(loadedUrl, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newCards = doc.querySelectorAll('.vm-brand-products__grid > .vm-brand-pcard');
        var firstOfBatch = null;
        for (var i = 0; i < newCards.length; i++) {
          var card = document.importNode(newCards[i], true);
          grid.appendChild(card);
          if (!firstOfBatch) firstOfBatch = card;
        }
        registerBatch(loadedUrl, doc.title, firstOfBatch, grid.querySelectorAll('.vm-brand-pcard:not([data-vm-page])'));

        var newNext = doc.getElementById('vm-brand-inf-next');
        loading = false;
        var loadedPage = pageNumberOf(loadedUrl);
        if (newNext && newCards.length) {
          nextUrl = newNext.href;
          hideSpinner();
          announce('Loaded ' + newCards.length + ' more products, page ' + loadedPage +
            (isNaN(totalPages) ? '' : ' of ' + totalPages) + '.');
        } else {
          finish();
        }
        queueSync();
        // Only if the shopper kept scrolling while this page was in flight.
        maybeLoad();
      })
      .catch(function () {
        // A failed fetch leaves the (clipped) server pager as the way on: reveal it.
        loading = false;
        nextUrl = null;
        hideSpinner();
        if (pager) pager.classList.add('vm-brand-pagination--shown');
      });
  }
  // No IntersectionObserver: it fires on a viewport resize with no scroll (how crawler
  // renderers expand the page), and it misses a sentinel skipped by a fast jump. The
  // scroll listener above + the input gate cover both.
})();
